import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { propertyAPI, agentAPI, appointmentAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { GlassCard, StatusBadge } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  MapPin, Building2, Users, Calendar as CalendarIcon,
  ArrowLeft, Home, DollarSign, Check, Sparkles,
} from 'lucide-react';
import PropertyMap from '../../components/common/PropertyMap';

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [property, setProperty] = useState(null);
  const [units, setUnits] = useState([]);
  const [stats, setStats] = useState({});
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formAgentId, setFormAgentId] = useState('');
  const [formDate, setFormDate] = useState(null);
  const [formTime, setFormTime] = useState('');
  const [formNotes, setFormNotes] = useState('');

  const loadData = useCallback(async () => {
    try {
      const propRes = await propertyAPI.getProperty(id);
      setProperty(propRes.data);
      const availRes = await propertyAPI.getAvailability(id);
      setUnits(availRes.data.units || []);
      setStats(availRes.data.stats || {});
      const agentsRes = await agentAPI.getAgents();
      setAgents(agentsRes.data || []);
    } catch (error) {
      toast.error('Failed to load property');
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async () => {
    if (!formAgentId || !formDate || !formTime) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      await appointmentAPI.createAppointment({
        property_id: id,
        agent_id: formAgentId,
        preferred_date: format(formDate, 'yyyy-MM-dd'),
        preferred_time: formTime,
        notes: formNotes,
      });
      toast.success('Appointment request submitted!');
      setShowModal(false);
      setFormAgentId(''); setFormDate(null); setFormTime(''); setFormNotes('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create appointment');
    }
  };

  const timeSlots = ['09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00', '18:00'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4E50FF]"></div>
      </div>
    );
  }

  if (!property) return null;

  return (
    <div className="space-y-6" data-testid="property-detail">
      <Button variant="ghost" onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-900">
        <ArrowLeft className="w-4 h-4 mr-2" />Back
      </Button>

      {/* Hero */}
      <div className="relative h-64 md:h-80 rounded-2xl overflow-hidden bg-gray-200">
        {property.images?.[0] && (
          <img src={property.images[0]} alt={property.name} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <span className="px-3 py-1 rounded-lg text-xs font-medium bg-[#4E50FF] text-white capitalize mb-3 inline-block">
            {property.property_type}
          </span>
          <h1 className="text-3xl font-bold text-white mb-2">{property.name}</h1>
          <div className="flex items-center gap-1 text-gray-200">
            <MapPin className="w-4 h-4" />{property.location}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Address & Map */}
          {property.address && (
            <GlassCard>
              <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-red-400" />Location
              </h2>
              <p className="text-gray-600 mb-4" data-testid="property-address">{property.address}</p>
              <PropertyMap
                address={property.address}
                propertyName={property.name}
                latitude={property.latitude}
                longitude={property.longitude}
                height="300px"
              />
            </GlassCard>
          )}

          <GlassCard>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Overview</h2>
            <p className="text-gray-600 mb-6">{property.description}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 rounded-xl bg-gray-50 border border-gray-100">
                <Building2 className="w-6 h-6 text-[#4E50FF] mx-auto mb-2" />
                <p className="text-lg font-semibold text-gray-900">{property.total_units}</p>
                <p className="text-xs text-gray-500">Total Units</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-gray-50 border border-gray-100">
                <Home className="w-6 h-6 text-[#4E50FF] mx-auto mb-2" />
                <p className="text-lg font-semibold text-gray-900 capitalize">{property.property_type}</p>
                <p className="text-xs text-gray-500">Type</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-gray-50 border border-gray-100">
                <Users className="w-6 h-6 text-[#4E50FF] mx-auto mb-2" />
                <p className="text-lg font-semibold text-gray-900 truncate">{property.developer}</p>
                <p className="text-xs text-gray-500">Developer</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                <DollarSign className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-lg font-semibold text-emerald-700">{property.commission_percentage || 0}%</p>
                <p className="text-xs text-gray-500">Commission</p>
              </div>
            </div>
          </GlassCard>

          <GlassCard>
            <Tabs defaultValue="overview">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Unit Availability</h2>
                <TabsList>
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="units">All Units</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="overview">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                    <p className="text-2xl font-bold text-emerald-700">{stats.available || 0}</p>
                    <p className="text-sm text-gray-500">Available</p>
                  </div>
                  <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
                    <p className="text-2xl font-bold text-amber-700">{stats.reserved || 0}</p>
                    <p className="text-sm text-gray-500">Reserved</p>
                  </div>
                  <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
                    <p className="text-2xl font-bold text-blue-700">{stats.booked || 0}</p>
                    <p className="text-sm text-gray-500">Booked</p>
                  </div>
                  <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
                    <p className="text-2xl font-bold text-gray-700">{stats.sold || 0}</p>
                    <p className="text-sm text-gray-500">Sold</p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="units">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Unit</TableHead>
                        <TableHead>Floor</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {units.length > 0 ? units.map((unit) => (
                        <TableRow key={unit.id}>
                          <TableCell className="font-medium">{unit.unit_number}</TableCell>
                          <TableCell>{unit.floor}</TableCell>
                          <TableCell>{unit.unit_type}</TableCell>
                          <TableCell>{unit.size_sqft} sqft</TableCell>
                          <TableCell className="text-[#4E50FF] font-mono">{formatCurrency(unit.price)}</TableCell>
                          <TableCell><StatusBadge status={unit.status} /></TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-gray-400 py-8">No units available</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </Tabs>
          </GlassCard>

          {property.amenities && property.amenities.length > 0 && (
            <GlassCard>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Amenities</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {property.amenities.map((amenity, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-gray-600">
                    <Check className="w-4 h-4 text-emerald-500" />{amenity}
                  </div>
                ))}
              </div>
            </GlassCard>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <GlassCard className="sticky top-6">
            <div className="text-center mb-6">
              <p className="text-sm text-gray-500 mb-1">Price Range</p>
              <p className="text-2xl font-bold text-[#4E50FF] font-mono">
                {formatCurrency(property.price_range_min)}
              </p>
              <p className="text-gray-400">to</p>
              <p className="text-2xl font-bold text-[#4E50FF] font-mono">
                {formatCurrency(property.price_range_max)}
              </p>
            </div>

            {user?.role === 'agent' ? (
              <Button
                onClick={() => {
                  // Use window.location to avoid Google Maps interfering with React Router navigation
                  window.location.href = `/bookings/create?property_id=${property.id}`;
                }}
                className="w-full bg-[#4E50FF] hover:bg-[#3436CC] text-white"
                data-testid="create-booking-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />Create Booking
              </Button>
            ) : user ? (
              <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogTrigger asChild>
                  <Button className="w-full bg-[#4E50FF] hover:bg-[#3436CC] text-white" data-testid="book-appointment-btn">
                    <CalendarIcon className="w-4 h-4 mr-2" />Book Viewing
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Book a Viewing</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Select Agent *</Label>
                      <Select value={formAgentId} onValueChange={setFormAgentId}>
                        <SelectTrigger data-testid="select-agent"><SelectValue placeholder="Choose an agent" /></SelectTrigger>
                        <SelectContent>
                          {agents.map((agent) => (
                            <SelectItem key={agent.id} value={agent.id}>{agent.full_name} ({agent.agent_code})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Preferred Date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal mt-1" data-testid="select-date">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formDate ? format(formDate, 'PPP') : 'Pick a date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar mode="single" selected={formDate} onSelect={setFormDate} disabled={(date) => date < new Date()} initialFocus />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label>Preferred Time *</Label>
                      <Select value={formTime} onValueChange={setFormTime}>
                        <SelectTrigger data-testid="select-time"><SelectValue placeholder="Select time" /></SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((time) => (<SelectItem key={time} value={time}>{time}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea value={formNotes} onChange={(e) => setFormNotes(e.target.value)} placeholder="Any special requests..." />
                    </div>
                    <Button onClick={handleSubmit} className="w-full bg-[#4E50FF] hover:bg-[#3436CC] text-white" data-testid="submit-appointment">
                      Submit Request
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            ) : (
              <Button onClick={() => navigate('/login')} className="w-full bg-[#4E50FF] hover:bg-[#3436CC] text-white">
                Login to Book
              </Button>
            )}

            <div className="mt-4 p-3 bg-emerald-50 rounded-xl text-center">
              <p className="text-xs text-gray-500">Agent Commission</p>
              <p className="text-lg font-bold text-emerald-700">{property.commission_percentage || 0}%</p>
            </div>
          </GlassCard>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
